/**
 * Headings of one note, for the sidebar outline. Lines are scanned rather than parsed:
 * the outline follows half-typed Markdown as it is written, and a `#` inside a code fence
 * or front matter is text, not a heading.
 */
export type OutlineHeading = { level: number; text: string; line: number };

const FENCE = /^ {0,3}(```+|~~~+)/;
const ATX = /^ {0,3}(#{1,6})(?:[ \t]+(.*))?$/;
const SETEXT = /^ {0,3}(=+|-+)[ \t]*$/;

/** What a heading says once its Markdown is taken off, which is what a list can show. */
export function headingText(source: string): string {
  return source
    .replace(/[ \t]+#+[ \t]*$/, "")
    .replace(/!?\[\[([^\]|]*)\|([^\]]*)\]\]/g, "$2")
    .replace(/!?\[\[([^\]]*)\]\]/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*\*|___)(.+?)\1/g, "$2")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(\*|_)(.+?)\1/g, "$2")
    .replace(/~~(.+?)~~/g, "$1")
    .replace(/`+([^`]+)`+/g, "$1")
    .trim();
}

export function outlineHeadings(source: string): OutlineHeading[] {
  const lines = source.split("\n");
  const headings: OutlineHeading[] = [];
  let fence = "";
  let start = 0;
  // Front matter opens on the very first line, so nothing inside it can be a heading.
  if (lines[0]?.trimEnd() === "---") {
    const end = lines.findIndex(
      (line, index) => index > 0 && ["---", "..."].includes(line.trimEnd()),
    );
    if (end > 0) start = end + 1;
  }
  for (let index = start; index < lines.length; index++) {
    const line = lines[index]!.trimEnd();
    const marker = FENCE.exec(line)?.[1];
    if (fence) {
      // A fence closes on a run of its own character at least as long as the opener.
      if (marker && marker[0] === fence[0] && marker.length >= fence.length) fence = "";
      continue;
    }
    if (marker) {
      fence = marker;
      continue;
    }
    const atx = ATX.exec(line);
    if (atx) {
      const text = headingText(atx[2] ?? "");
      if (text) headings.push({ level: atx[1]!.length, text, line: index + 1 });
      continue;
    }
    // An underlined heading belongs to the line above, which has to be ordinary text.
    const underline = SETEXT.exec(line)?.[1];
    const above = index > start ? lines[index - 1]!.trimEnd() : "";
    if (!underline || !above.trim() || ATX.test(above) || FENCE.test(above)) continue;
    const text = headingText(above);
    if (text) headings.push({ level: underline[0] === "=" ? 1 : 2, text, line: index });
  }
  return headings;
}

/** The heading a line sits under, so the outline can say where you are. */
export function headingAtLine(
  headings: readonly OutlineHeading[],
  line: number,
): OutlineHeading | null {
  let found: OutlineHeading | null = null;
  for (const heading of headings) {
    if (heading.line > line) break;
    found = heading;
  }
  return found;
}

/**
 * The lines of a heading's section, 1-based and inclusive: from the heading to the last line
 * with text before the next heading at its level or above, or before the end of the note.
 */
export function headingSection(
  source: string,
  headings: readonly OutlineHeading[],
  heading: OutlineHeading,
): { from: number; to: number } {
  const lines = source.split("\n");
  const next = headings.find((other) => other.line > heading.line && other.level <= heading.level);
  let to = next ? next.line - 1 : lines.length;
  while (to > heading.line && !lines[to - 1]!.trim()) to--;
  return { from: heading.line, to };
}

/**
 * The edits that move a heading and every heading in its section `by` levels, so its
 * sub-headings stay under it: # is level 1 and ###### level 6. An underlined heading
 * keeps its underline at levels 1 and 2 and takes #s below them. Null when a heading would
 * go past either end.
 */
export function shiftSection(
  source: string,
  headings: readonly OutlineHeading[],
  heading: OutlineHeading,
  by: 1 | -1,
): { from: number; to: number; insert: string }[] | null {
  const lines = source.split("\n");
  const starts: number[] = [];
  let offset = 0;
  for (const line of lines) {
    starts.push(offset);
    offset += line.length + 1;
  }
  const section = headingSection(source, headings, heading);
  const moved = headings.filter((other) => other.line >= section.from && other.line <= section.to);
  if (moved.some((other) => other.level + by < 1 || other.level + by > 6)) return null;
  return moved.map((other) => {
    const level = other.level + by;
    const text = lines[other.line - 1]!;
    const from = starts[other.line - 1]!;
    const atx = /^( {0,3})(#{1,6})/.exec(text);
    if (atx) return { from: from + atx[1]!.length, to: from + atx[0].length, insert: "#".repeat(level) };
    // Underlined: = for level 1, - for level 2, and #s from level 3 down.
    const underline = lines[other.line]!;
    const end = starts[other.line]! + underline.length;
    if (level <= 2)
      return { from: starts[other.line]!, to: end, insert: (level === 1 ? "=" : "-").repeat(underline.trim().length) };
    return { from, to: end, insert: `${"#".repeat(level)} ${text.trim()}` };
  });
}

/** A heading's anchor as GitHub writes it: lower case, spaces as hyphens, other punctuation gone. */
export function headingSlug(text: string): string {
  return text.trim().toLocaleLowerCase().replace(/[^\p{L}\p{N}\s_-]/gu, "").replace(/\s/g, "-");
}

/**
 * The heading a link names, by its text in any case (`Goals and Risks`) or by its anchor
 * (`goals-and-risks`), where a repeated heading's later ones are `-1`, `-2`, as on GitHub.
 */
export function findHeading(headings: readonly OutlineHeading[], name: string): OutlineHeading | null {
  const wanted = name.trim().toLocaleLowerCase();
  const byText = headings.find((heading) => heading.text.toLocaleLowerCase() === wanted);
  if (byText) return byText;
  const anchor = headingSlug(name);
  const seen = new Map<string, number>();
  for (const heading of headings) {
    const slug = headingSlug(heading.text);
    const count = seen.get(slug) ?? 0;
    seen.set(slug, count + 1);
    if ((count ? `${slug}-${count}` : slug) === anchor) return heading;
  }
  return null;
}
